import { useCallback, useEffect } from "react";
import { toast } from "react-toastify";

import { useActionHook, usePrompt } from "hooks";
import { usePortfolioConstructionActions } from "store";
import {
  ActiveSliceType,
  PolicyAssetClass,
  SliceGroup,
  SliceInvestor,
  TargetPolicyItem,
  TargetPolicySaveRequest
} from "store/types";

import { TargetPolicyPrompt } from "../Target-policy-prompt/TargetPolicyPrompt";

interface UseTargetPolicyParams {
  activeSliceType: ActiveSliceType | null;
  activeSliceKey: string | null;
  activeSliceName: string;
  selectedInvestorId: SliceInvestor["id"] | null;
  selectedGroupId: SliceGroup["id"] | null;
  policyAssetClasses: PolicyAssetClass[];
  canEditTargetPolicy: boolean;
}

const resolveSlice = (
  activeSliceType: ActiveSliceType | null,
  selectedInvestorId: SliceInvestor["id"] | null,
  selectedGroupId: SliceGroup["id"] | null
): { sliceId: number; sliceType: "investors" | "slices" } | null => {
  if (activeSliceType === "investor" && selectedInvestorId !== null) {
    return { sliceId: Number(selectedInvestorId), sliceType: "investors" };
  }
  if (activeSliceType === "group" && selectedGroupId !== null) {
    return { sliceId: Number(selectedGroupId), sliceType: "slices" };
  }
  return null;
};

// Owns the target policy for the active slice: loads it on slice change and
// drives the create/edit prompt. Returns the loaded values (consumed by the
// drift table) and the prompt opener.
export const useTargetPolicy = ({
  activeSliceType,
  activeSliceKey,
  activeSliceName,
  selectedInvestorId,
  selectedGroupId,
  policyAssetClasses,
  canEditTargetPolicy
}: UseTargetPolicyParams) => {
  const openPrompt = usePrompt();
  const { getTargetPolicyAction, replaceTargetPolicyAction } = usePortfolioConstructionActions();

  const fetchTargetPolicy = useCallback(async (): Promise<TargetPolicyItem[] | null> => {
    const slice = resolveSlice(activeSliceType, selectedInvestorId, selectedGroupId);
    if (!slice) return null;
    try {
      return await getTargetPolicyAction(slice.sliceId, slice.sliceType);
    } catch {
      return null;
    }
  }, [activeSliceType, selectedInvestorId, selectedGroupId, getTargetPolicyAction]);

  const {
    data: targetPolicy = null,
    action: loadTargetPolicy,
    setData: setTargetPolicy
  } = useActionHook(fetchTargetPolicy);

  const onOpenTargetPolicy = useCallback(async () => {
    if (!activeSliceKey || !canEditTargetPolicy) return;
    const slice = resolveSlice(activeSliceType, selectedInvestorId, selectedGroupId);
    if (!slice) return;

    const mode: "create" | "edit" = targetPolicy && targetPolicy.length > 0 ? "edit" : "create";
    const result = await openPrompt<{
      mode?: "create" | "edit";
      sliceName: string;
      assetClasses: PolicyAssetClass[];
      initialPolicy?: TargetPolicyItem[];
      savePolicy: (values: TargetPolicySaveRequest) => Promise<void>;
      onSubmit: (values: TargetPolicySaveRequest) => void;
      onCancel: () => void;
    }>({
      Component: TargetPolicyPrompt,
      componentType: "modal",
      disableBackdropClose: true,
      props: {
        mode,
        sliceName: activeSliceName,
        assetClasses: policyAssetClasses,
        initialPolicy: targetPolicy ?? undefined,
        savePolicy: async (values) => {
          const saved = await replaceTargetPolicyAction(slice.sliceId, slice.sliceType, values);
          if (saved) setTargetPolicy(saved);
        }
      }
    });
    if (!result) return;
    toast.success("Target policy saved.");
  }, [
    activeSliceKey,
    activeSliceName,
    activeSliceType,
    targetPolicy,
    policyAssetClasses,
    canEditTargetPolicy,
    openPrompt,
    replaceTargetPolicyAction,
    selectedGroupId,
    selectedInvestorId,
    setTargetPolicy
  ]);

  // Reload when the active slice changes.
  useEffect(() => {
    loadTargetPolicy();
  }, [activeSliceKey, loadTargetPolicy]);

  return {
    targetPolicy,
    onOpenTargetPolicy
  };
};
